import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminApi, adminAuth } from '../lib/adminApi';
import { PasswordInput } from '../components/PasswordInput';
import { SEO } from '../components/SEO';

export function AdminLogin() {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  // Set once we know 2FA is enabled — the login endpoint returns
  // { needsTotp: true } on step 1 to tell us to prompt for a code.
  const [needsTotp, setNeedsTotp] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const res = await adminApi.login(password, needsTotp ? totpCode : undefined);
      if (res.needsTotp) {
        setNeedsTotp(true);
        setLoading(false);
        return;
      }
      if (!res.token) {
        setError('Unexpected response — please try again.');
        return;
      }
      adminAuth.token = res.token;
      navigate('/admin');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 p-6">
      <SEO title="Admin sign in — Dormant Accounts Filing" noindex />
      <div className="card w-full max-w-sm">
        <h1 className="text-xl font-semibold text-slate-900">Admin sign in</h1>
        <p className="mt-1 text-xs text-slate-500">For staff only.</p>
        <form onSubmit={handleSubmit} className="mt-6 space-y-4">
          <PasswordInput
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Admin password"
            autoFocus={!needsTotp}
            autoComplete="current-password"
            required
            readOnly={needsTotp}
          />
          {needsTotp && (
            <div>
              <input
                type="text"
                inputMode="numeric"
                pattern="\d{6}"
                maxLength={6}
                value={totpCode}
                onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ''))}
                placeholder="6-digit code"
                className="input text-center text-lg tracking-[0.4em]"
                autoFocus
                autoComplete="one-time-code"
                required
              />
              <p className="mt-1 text-[11px] text-slate-500">
                From your authenticator app (Google Authenticator, 1Password, etc.)
              </p>
            </div>
          )}
          {error && (
            <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">{error}</div>
          )}
          <button type="submit" disabled={loading} className="btn-primary w-full">
            {loading
              ? 'Signing in…'
              : needsTotp
                ? 'Verify code'
                : 'Sign in'}
          </button>
          {needsTotp && (
            <button
              type="button"
              onClick={() => {
                setNeedsTotp(false);
                setTotpCode('');
                setError(null);
              }}
              className="w-full text-xs text-slate-500 hover:text-slate-900"
            >
              ← Start over
            </button>
          )}
        </form>
      </div>
    </div>
  );
}
